<script setup lang="ts">
/**
 * UNavTree · 页内导航树（菜单搜索 + 常用菜单 + 分组功能树）
 * ------------------------------------------------------------
 * 形式对齐应用页 RailNav 的「系统菜单」段：
 * - 菜单搜索：功能名 / 组名 / 副标题过滤，跨组平铺命中
 * - 常用菜单：功能行尾 ☆ 收藏置顶（localStorage 持久化，固定上限 10，与使用行为无关）
 * - 分组树：L1 分组点击展开/收起（段头一键全部展开/收起），L2 功能项高亮当前页
 * 用于无全局 rail 的独立主页面（/fde、/product/*）的页内导航；与 RailNav 数据解耦：
 * 分组数据由调用方传入，各页收藏各自存 localStorage（favKey 区分）。
 */
import { ref, computed, watch } from 'vue'
import { useToast } from '@/design/ui/UToast'

export interface NavTreeItem { id: string; name: string; sub?: string; tag?: string }
export interface NavTreeGroup { id: string; name: string; ic: string; color: string; items: NavTreeItem[] }

const props = withDefaults(defineProps<{
  groups: NavTreeGroup[]
  activeId: string
  favKey: string
  searchPlaceholder?: string
  /* 常驻左栏使用（如壳层 RailNav）时置 true：禁用 ≤960px 窄屏折叠，任何视口下都完整展开 */
  neverCollapse?: boolean
}>(), { searchPlaceholder: '搜索菜单 / 功能…', neverCollapse: false })
const emit = defineEmits<{ select: [id: string] }>()
const toast = useToast().show

/* ---------- 菜单搜索（功能名 / 组名 / 副标题过滤，跨组平铺命中） ---------- */
const kw = ref('')
const hits = computed(() => {
  const k = kw.value.trim().toLowerCase()
  if (!k) return null
  const out: { item: NavTreeItem; group: NavTreeGroup }[] = []
  for (const g of props.groups) for (const it of g.items)
    if (it.name.toLowerCase().includes(k) || g.name.toLowerCase().includes(k) || (it.sub ?? '').toLowerCase().includes(k))
      out.push({ item: it, group: g })
  return out
})

/* ---------- 常用菜单（仅手动 ☆ 收藏；上限 10；数据失效自动过滤） ---------- */
interface Fav { id: string; name: string; ic: string; color: string }
const FAV_MAX = 10
function loadFavs(): Fav[] {
  try {
    const v = JSON.parse(localStorage.getItem(props.favKey) || '[]')
    return Array.isArray(v) ? v.slice(0, FAV_MAX) : []
  } catch { return [] }
}
const favs = ref<Fav[]>(loadFavs())
watch(favs, v => { try { localStorage.setItem(props.favKey, JSON.stringify(v)) } catch { /* 忽略 */ } }, { deep: true })
const itemBy = computed(() => {
  const m: Record<string, { item: NavTreeItem; group: NavTreeGroup }> = {}
  for (const g of props.groups) for (const it of g.items) m[it.id] = { item: it, group: g }
  return m
})
watch(itemBy, m => { favs.value = favs.value.filter(f => !!m[f.id]) }, { immediate: true })
function isFav(id: string) { return favs.value.some(f => f.id === id) }
function toggleFav(e: Event, it: NavTreeItem, g: NavTreeGroup) {
  e.stopPropagation()
  const i = favs.value.findIndex(f => f.id === it.id)
  if (i >= 0) favs.value = favs.value.filter((_, k) => k !== i)
  else if (favs.value.length >= FAV_MAX) { toast(`常用菜单最多固定 ${FAV_MAX} 个，请先取消一些再收藏`); return }
  else favs.value = [...favs.value, { id: it.id, name: it.name, ic: g.ic, color: g.color }]
}

/* ---------- 分组展开状态（项少默认全开；段头一键展开/收起） ---------- */
const openGroups = ref<Set<string>>(new Set(props.groups.map(g => g.id)))
function togGroup(id: string) {
  const n = new Set(openGroups.value); n.has(id) ? n.delete(id) : n.add(id)
  openGroups.value = n
}
const allOpen = computed(() => props.groups.length > 0 && props.groups.every(g => openGroups.value.has(g.id)))
function togAll() { openGroups.value = allOpen.value ? new Set() : new Set(props.groups.map(g => g.id)) }

/* ---------- 窄屏折叠：菜单收成「按钮 + 搜索框」一行，点开才展开完整导航 ---------- */
const menuOpen = ref(false)
const activeName = computed(() => itemBy.value[props.activeId]?.item.name || '菜单')
function onSelect(id: string) { menuOpen.value = false; emit('select', id) }
</script>

<template>
  <div class="unavtree" :class="{ open: menuOpen, 'no-narrow': neverCollapse }">
    <!-- 顶行：窄屏「菜单按钮 + 搜索框」；宽屏只显示搜索框 -->
    <div class="nav-bar">
      <button class="nav-tog" :title="menuOpen ? '收起菜单' : '展开菜单'" @click="menuOpen = !menuOpen">
        <i class="nt-ic">{{ menuOpen ? '▾' : '☰' }}</i><span class="nt-nm">{{ activeName }}</span>
      </button>
      <input v-model="kw" class="menu-search" type="text" :placeholder="searchPlaceholder" maxlength="20">
    </div>

    <!-- 窄屏收起态的收藏快捷排：单独平铺展示，点按直达；展开菜单或搜索时隐藏（避免与下方重复） -->
    <div v-if="favs.length && !hits && !menuOpen" class="fav-strip">
      <button v-for="f in favs" :key="f.id" class="fav-chip" :class="{ on: activeId === f.id }"
              :title="f.name" @click="onSelect(f.id)">
        <span class="fv-ic" :style="{ background: f.color }">{{ f.ic }}</span>
        <span class="fv-nm">{{ f.name }}</span>
      </button>
    </div>

    <!-- 搜索命中：跨组平铺功能项 -->
    <div v-if="hits" class="menu-tree hits">
      <div v-if="!hits.length" class="rail-empty">未找到匹配的功能</div>
      <button v-for="h in hits" :key="h.item.id" class="mt-row hit" :class="{ on: activeId === h.item.id }"
              :title="h.item.sub ? `${h.item.name} · ${h.item.sub}` : h.item.name" @click="onSelect(h.item.id)">
        <span class="mt-ic" :style="{ background: h.group.color }">{{ h.group.ic }}</span>
        <span class="hit-b">
          <b>{{ h.item.name }}</b>
          <small>{{ h.group.name }}<template v-if="h.item.sub"> · {{ h.item.sub }}</template></small>
        </span>
      </button>
    </div>

    <div v-else class="nav-body">
      <!-- 常用菜单（收藏置顶，位于搜索栏与系统菜单之间；空态给收藏引导） -->
      <div class="rail-label"><span>常用菜单</span> <span class="ct">{{ favs.length }}</span></div>
      <div class="rail-chats favs">
        <button v-for="f in favs" :key="f.id" class="fav-item" :class="{ on: activeId === f.id }"
                :title="f.name" @click="onSelect(f.id)">
          <span class="fv-ic" :style="{ background: f.color }">{{ f.ic }}</span>
          <span class="fv-nm">{{ f.name }}</span>
          <i class="fv-x" title="取消收藏" @click.stop="favs = favs.filter(x => x.id !== f.id)">×</i>
        </button>
        <div v-if="!favs.length" class="rail-empty">点系统菜单功能行尾的 ☆ 收藏到这里</div>
      </div>

      <!-- 系统菜单（分组 → 功能项）；段头带一键展开/收起 -->
      <div class="rail-label">
        <span>系统菜单</span> <span class="ct">{{ groups.length }} 模块</span>
        <button v-if="groups.length" class="lab-act" :title="allOpen ? '全部收起' : '全部展开'"
                @click="togAll">{{ allOpen ? '全部收起' : '全部展开' }}</button>
      </div>
      <div class="menu-tree">
        <div v-for="g in groups" :key="g.id" class="mt-dom">
          <!-- L1 分组：点击展开/收起 -->
          <button class="mt-row lv1" @click="togGroup(g.id)">
            <i class="mt-arr" :class="{ open: openGroups.has(g.id) }">▶</i>
            <span class="mt-ic" :style="{ background: g.color }">{{ g.ic }}</span>
            <span class="mt-nm">{{ g.name }}</span>
          </button>
          <!-- L2 功能项 -->
          <template v-if="openGroups.has(g.id)">
            <button v-for="it in g.items" :key="it.id"
                    class="mt-row lv2" :class="{ on: activeId === it.id }"
                    :title="it.sub ? `${it.name} · ${it.sub}` : it.name" @click="onSelect(it.id)">
              <span class="mt-nm">{{ it.name }}<i v-if="it.tag" class="mt-tag">{{ it.tag }}</i></span>
              <i class="mt-fav" :class="{ on: isFav(it.id) }"
                 :title="isFav(it.id) ? '取消收藏' : '收藏到常用菜单'"
                 @click.stop="toggleFav($event, it, g)">{{ isFav(it.id) ? '★' : '☆' }}</i>
            </button>
          </template>
        </div>
      </div>
    </div>
  </div>
</template>

<style scoped>
.unavtree { display: flex; flex-direction: column; gap: 12px; height: 100%; min-height: 0; }

/* === 顶行：宽屏只显示搜索框（菜单按钮隐藏）；窄屏为「按钮 + 搜索框」 === */
.nav-bar { flex: none; display: contents; }
.nav-tog { display: none; }
.fav-strip { display: none; }
.nav-body { flex: 1; min-height: 0; display: flex; flex-direction: column; gap: 12px; }

/* === 菜单搜索（与应用页 RailNav 同规格；与其他搜索框一致：居中、两侧留白） === */
.menu-search {
  height: 28px; padding: 0 9px; font-size: var(--fs-2xs); flex: none;
  width: calc(100% - 20px); margin: 0 auto; box-sizing: border-box;
  border: 1px solid var(--line); border-radius: 7px;
  background: var(--surface); color: var(--ink);
}
.menu-search:focus { border-color: var(--accent-line); outline: none; }

/* === 分组标签（段头带一键展开/收起动作） === */
.rail-label {
  display: flex; align-items: baseline; gap: 6px; flex: none;
  padding: 0 2px; font-size: 10.5px; font-weight: 600;
  color: var(--ink-4); letter-spacing: .7px;
}
.rail-label .ct { color: var(--ink-4); font-weight: 400; }
.lab-act {
  margin-left: auto; align-self: center;
  font-size: 10px; font-weight: 550; letter-spacing: 0;
  color: var(--ink-4); padding: 2px 8px; border-radius: 999px; cursor: pointer;
}
.lab-act:hover { color: var(--accent); background: var(--accent-soft); }

/* === 常用菜单（按内容自适应，不超过约 1/3 高，剩余空间让给功能树） === */
.rail-chats { flex: 1; overflow: auto; min-height: 0; display: flex; flex-direction: column; gap: 2px; }
.rail-chats.favs { flex: none; max-height: 30vh; }
.rail-empty { padding: var(--sp-3); font-size: var(--fs-xs); color: var(--ink-4); text-align: center; line-height: 1.7; }
.fav-item {
  display: flex; align-items: center; gap: 8px; width: 100%;
  padding: 6px 8px; border-radius: var(--r-s);
  font-size: var(--fs-xs); color: var(--ink-2); text-align: left;
}
.fav-item:hover { background: var(--hover); }
.fav-item.on { background: var(--accent-soft); color: var(--accent); }
.fv-ic {
  width: 18px; height: 18px; flex: none; border-radius: 5px;
  color: #fff; font-size: 10px; font-weight: 650; display: grid; place-items: center;
}
.fv-nm { flex: 1; min-width: 0; overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }
.fv-x { flex: none; font-style: normal; font-size: 13px; color: var(--ink-4); width: 16px; text-align: center; opacity: 0; }
.fav-item:hover .fv-x { opacity: 1; }
.fv-x:hover { color: #de4343; }

/* === 分组功能树（行规格与应用页 RailNav 对齐：7px 12px / 12.8px / 图标 21px）；
   层级引导线：L2 在 L1 缩进档位中点画 1px 竖线（线位 24）；
   引导线用 background-image 实现，hover/选中态必须用 background-color 以免简写清掉竖线 === */
.menu-tree { flex: 1; overflow: auto; min-height: 0; display: flex; flex-direction: column; gap: 1px; }
.mt-row {
  display: flex; align-items: center; gap: 8px; width: 100%; text-align: left;
  padding: 7px 12px; border-radius: var(--r-s);
  font-size: 12.8px; color: var(--ink-2); min-width: 0;
}
.mt-row:hover { background-color: var(--hover); }
.mt-row.on { background-color: var(--accent-soft); color: var(--accent); font-weight: 550; }
.mt-row.on .mt-nm { color: var(--accent); }
.mt-row.lv1 { font-weight: 550; color: var(--ink); }
.mt-row.lv2 {
  padding-left: 36px; color: var(--ink-3);
  background: linear-gradient(var(--line-soft), var(--line-soft)) no-repeat 24px 0 / 1px 100%;
}
.mt-arr { font-style: normal; font-size: 8.5px; width: 11px; height: 11px; flex: none; color: var(--ink-4); display: grid; place-items: center; transition: transform .15s; line-height: 1; }
.mt-arr.open { transform: rotate(90deg); color: var(--accent); }
.mt-ic {
  width: 21px; height: 21px; flex: none; border-radius: 7px;
  color: #fff; font-size: 11px; font-weight: 700; display: grid; place-items: center;
}
.mt-nm { flex: 1; min-width: 0; overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }
.mt-tag { font-style: normal; margin-left: 5px; padding: 0 4px; border-radius: 4px; font-size: 8.5px; font-weight: 700; color: var(--accent); background: var(--accent-soft); vertical-align: 1px; }
.mt-fav { flex: none; font-style: normal; font-size: 14.5px; color: var(--ink-4); width: 20px; text-align: center; transition: color .12s ease; cursor: pointer; }
.mt-row:hover .mt-fav { color: var(--ink-3); }
.mt-fav.on { color: #f5a623; }
.mt-fav:hover { color: #f5a623; }

/* === 搜索命中行（图标 + 名称/组名·副标题 双行） === */
.mt-row.hit { padding-left: 12px; }
.hit-b { flex: 1; min-width: 0; }
.hit-b b { display: block; font-size: 12.8px; font-weight: 550; color: var(--ink-2); overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }
.hit-b small { display: block; font-size: 10.3px; color: var(--ink-4); overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }
.mt-row.hit.on .hit-b b { color: var(--accent); }

/* === 窄屏（≤960px，与 SolWorkbench / FdeView 响应式断点一致）：
   菜单收成「按钮 + 搜索框」一行，默认不占首屏；点按钮展开完整导航，选项后自动收起 === */
@media (max-width: 960px) {
  .nav-bar { display: flex; align-items: center; gap: 8px; }
  .nav-tog {
    display: inline-flex; align-items: center; gap: 6px; flex: none;
    height: 28px; padding: 0 10px; max-width: 45%;
    border: 1px solid var(--line); border-radius: 7px;
    background: var(--surface); color: var(--ink-2);
    font-size: var(--fs-2xs); font-weight: 550; cursor: pointer;
  }
  .nav-tog:hover { border-color: var(--accent-line); color: var(--accent); }
  .nt-ic { font-style: normal; font-size: 11px; line-height: 1; }
  .nt-nm { overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }
  .menu-search { flex: 1; width: auto; margin: 0; }
  /* 收藏快捷排：收起态单独平铺展示，横向可滚，点按直达 */
  .fav-strip {
    display: flex; gap: 6px; flex: none;
    overflow-x: auto; padding-bottom: 2px;
  }
  .fav-chip {
    display: inline-flex; align-items: center; gap: 6px; flex: none;
    height: 28px; padding: 0 10px;
    border: 1px solid var(--line); border-radius: 7px;
    background: var(--surface); color: var(--ink-2);
    font-size: var(--fs-2xs); cursor: pointer;
  }
  .fav-chip:hover { border-color: var(--accent-line); color: var(--accent); }
  .fav-chip.on { background: var(--accent-soft); border-color: var(--accent-line); color: var(--accent); font-weight: 550; }
  /* 未展开时隐藏收藏与功能树（搜索命中列表不受限，输入即出结果） */
  .unavtree:not(.open) .nav-body { display: none; }
  /* 展开时限高内滚，避免把内容整体顶出首屏 */
  .unavtree.open .nav-body { max-height: 55vh; }
}

/* === no-narrow：常驻左栏（壳层 RailNav）时禁用窄屏折叠，任何视口保持宽屏完整形态 === */
.unavtree.no-narrow .nav-bar { display: contents !important; }
.unavtree.no-narrow .nav-tog, .unavtree.no-narrow .fav-strip { display: none !important; }
.unavtree.no-narrow .menu-search { flex: none !important; width: calc(100% - 20px) !important; margin: 0 auto !important; }
.unavtree.no-narrow .nav-body { display: flex !important; max-height: none !important; }
</style>
